import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, RefreshCw, ArrowRightLeft, CheckCircle2 } from "lucide-react";
import ContratoCard, { computeContratoSim, idadeAnos } from "./ContratoCard";

export default function ContratosList({
  contratos,
  loading,
  expandedRows,
  onToggleRow,
  onEdit,
  onDelete,
  contratosOcultos,
  checklistAnalise,
  clienteRestricoesInternas,
  percentualDesconto,
  onAnalisarContrato,
  loadingChecklist,
  bancos,
  tabelasComissao,
  instituicaoSelecionada,
  clienteNascimento,
  onAddOcorrencia,
  onDeleteOcorrencia,
}) {
  // Filtrar contratos ocultos
  const contratosFiltrados = contratos.filter(c => !contratosOcultos?.[c.id]);

  // Ordenar: contratos com simulação APROVADA primeiro; entre aprovados, maior Valor Liberado
  const _idadeLista = idadeAnos(clienteNascimento);
  const _simCache = new Map();
  const _getSim = (c) => { if (!_simCache.has(c.id)) _simCache.set(c.id, computeContratoSim(c, bancos || [], tabelasComissao || [], instituicaoSelecionada || "", _idadeLista)); return _simCache.get(c.id); };
  const _score = (c) => { const s = _getSim(c); const ap = (s.port.aprovado || s.refin.aprovado) ? 1 : 0; const vals = [...s.port.rows, ...s.refin.rows].filter((x) => x.av.aprovado).map((x) => x.r.valorTroco || 0); const best = vals.length ? Math.max(...vals) : -Infinity; return { ap, best }; };
  const contratosOrdenados = [...contratosFiltrados].sort((a, b) => { const A = _score(a), B = _score(b); return (B.ap - A.ap) || (B.best - A.best); });

  // Filtro de aprovados por tipo de operação
  const [filtroAprovado, setFiltroAprovado] = useState(null);
  const _countPort = contratosFiltrados.filter((c) => _getSim(c).port.aprovado).length;
  const _countRefin = contratosFiltrados.filter((c) => _getSim(c).refin.aprovado).length;
  const contratosExibidos = filtroAprovado
    ? contratosOrdenados.filter((c) => { const s = _getSim(c); return filtroAprovado === "PORTABILIDADE" ? s.port.aprovado : s.refin.aprovado; })
    : contratosOrdenados;

  return (
    <Card>
      <CardContent className="pt-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : contratosFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Nenhum contrato encontrado</h3>
            <p className="text-muted-foreground">
              {contratos.length > 0 
                ? "Todos os contratos estão ocultos"
                : 'Clique em "Novo Contrato" para adicionar.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap" data-testid="filtro-aprovados-toolbar">
              <Button size="sm" variant={filtroAprovado === null ? "default" : "outline"} onClick={() => setFiltroAprovado(null)} data-testid="filtro-todos">
                Todos ({contratosFiltrados.length})
              </Button>
              <Button size="sm" variant={filtroAprovado === "PORTABILIDADE" ? "default" : "outline"} className="gap-1" onClick={() => setFiltroAprovado((p) => (p === "PORTABILIDADE" ? null : "PORTABILIDADE"))} data-testid="filtro-portabilidade-aprovada">
                <ArrowRightLeft className="h-4 w-4" /> Só Portabilidade Aprovada ({_countPort})
              </Button>
              <Button size="sm" variant={filtroAprovado === "REFINANCIAMENTO" ? "default" : "outline"} className="gap-1" onClick={() => setFiltroAprovado((p) => (p === "REFINANCIAMENTO" ? null : "REFINANCIAMENTO"))} data-testid="filtro-refin-aprovado">
                <RefreshCw className="h-4 w-4" /> Só Refin Aprovado ({_countRefin})
              </Button>
            </div>
            {contratosExibidos.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground" data-testid="filtro-sem-resultados">
                <CheckCircle2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Nenhum contrato com {filtroAprovado === "PORTABILIDADE" ? "Portabilidade" : "Refin"} aprovada.</p>
              </div>
            ) : contratosExibidos.map((contrato) => (
              <ContratoCard
                key={contrato.id}
                contrato={contrato}
                isExpanded={!!expandedRows?.[contrato.id]}
                onToggle={() => onToggleRow && onToggleRow(contrato.id)}
                onEdit={onEdit}
                onDelete={onDelete}
                checklistAnalise={checklistAnalise}
                clienteRestricoesInternas={clienteRestricoesInternas}
                percentualDesconto={percentualDesconto}
                onAnalisarContrato={onAnalisarContrato}
                loadingChecklist={loadingChecklist}
                bancos={bancos}
                tabelasComissao={tabelasComissao}
                instituicaoSelecionada={instituicaoSelecionada}
                clienteNascimento={clienteNascimento}
                onAddOcorrencia={onAddOcorrencia}
                onDeleteOcorrencia={onDeleteOcorrencia}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Componente de resumo de contratos
export function ContratosResumo({ contratos, contratosOcultos }) {
  const contratosFiltrados = contratos.filter(c => !contratosOcultos?.[c.id]);
  const totalOcultos = contratos.length - contratosFiltrados.length;
  
  const internos = contratosFiltrados.filter(c => c.tipo === "INTERNO").length;
  const externos = contratosFiltrados.filter(c => c.tipo === "EXTERNO").length;
  const ativos = contratosFiltrados.filter(c => c.situacao === "ATIVO").length;
  
  return (
    <div className="flex items-center gap-4 text-sm text-muted-foreground">
      <span>Total: <strong className="text-foreground">{contratosFiltrados.length}</strong></span>
      <span>Internos: <strong className="text-blue-600">{internos}</strong></span>
      <span>Externos: <strong className="text-purple-600">{externos}</strong></span>
      <span>Ativos: <strong className="text-green-600">{ativos}</strong></span>
      {totalOcultos > 0 && (
        <span className="text-orange-600">({totalOcultos} ocultos)</span>
      )}
    </div>
  );
}
